import type { ComponentType } from 'react';
import { ServiceDetailPage } from '@/components/ServiceDetailPage';

export type ManAndVanPageConfig = {
  path: string;
  navLabel: string;
  cardTitle: string;
  title: string;
  description: string;
  intro: string;
  highlights: string[];
  process: string[];
  idealFor: string[];
};

function createManAndVanPage(config: ManAndVanPageConfig): ComponentType {
  return function ManAndVanPage() {
    return (
      <ServiceDetailPage
        activePath={config.path}
        serviceMenuPath="/services/man-and-van"
        title={config.title}
        description={config.description}
        intro={config.intro}
        highlights={config.highlights}
        process={config.process}
        idealFor={config.idealFor}
      />
    );
  };
}

const baseHighlights = (location: string): string[] => [
  `Friendly, professional Man and Van service across ${location}`,
  'Ideal for small moves, single items, and flexible collection or delivery',
  'Clean, well-maintained vans with experienced, insured movers',
  'Transparent pricing with no hidden fees or surprise charges',
];

const baseProcess = (location: string): string[] => [
  `We confirm your ${location} pickup and drop-off addresses, access, and timings.`,
  'You get a clear quote based on van size, distance, and estimated time.',
  'Our team arrives on schedule, handles loading, and transports your items with care.',
  'We unload at your new address, place items where needed, and make sure you are happy.',
];

const baseIdealFor = (location: string): string[] => [
  `Students, tenants, and homeowners in ${location} moving smaller loads`,
  'Single-item deliveries, furniture collections, and eBay/Facebook Marketplace pickups',
  'Small flat moves, room moves, and short-notice relocations',
];

export const manAndVanPageConfigs: ManAndVanPageConfig[] = [
  {
    path: '/services/man-and-van/staffordshire',
    navLabel: 'Staffordshire',
    cardTitle: 'Man and Van in Staffordshire',
    title: 'Man and Van in Staffordshire',
    description: 'Friendly, reliable Man and Van service across Staffordshire from VIP House Movers, built for small moves, single items, and flexible schedules.',
    intro: 'Whether you are moving between towns in Staffordshire or just need a hand shifting a few items, our Man and Van service gives you a cost-effective, professional option with the same premium care as our larger removals.',
    highlights: baseHighlights('Staffordshire'),
    process: baseProcess('Staffordshire'),
    idealFor: baseIdealFor('Staffordshire'),
  },
  {
    path: '/services/man-and-van/wolverhampton',
    navLabel: 'Wolverhampton',
    cardTitle: 'Man and Van in Wolverhampton',
    title: 'Man and Van in Wolverhampton',
    description: 'Trusted Man and Van in Wolverhampton by VIP House Movers, perfect for flat moves, single items, and short-notice pickups across the city.',
    intro: 'As a Wolverhampton-based removals company, we know the local streets, parking, and property types well, which helps us deliver a faster, smoother Man and Van service for our neighbours.',
    highlights: baseHighlights('Wolverhampton'),
    process: baseProcess('Wolverhampton'),
    idealFor: baseIdealFor('Wolverhampton'),
  },
  {
    path: '/services/man-and-van/birmingham',
    navLabel: 'Birmingham',
    cardTitle: 'Man and Van in Birmingham',
    title: 'Man and Van in Birmingham',
    description: 'Dependable Man and Van in Birmingham from VIP House Movers, ideal for flat moves, one-item jobs, and quick collections across the city.',
    intro: 'Birmingham moves often involve tight parking, one-way streets, and flats with restricted access. We plan each Man and Van booking around those realities so your move stays smooth and stress-free.',
    highlights: baseHighlights('Birmingham'),
    process: baseProcess('Birmingham'),
    idealFor: baseIdealFor('Birmingham'),
  },
  {
    path: '/services/man-and-van/walsall',
    navLabel: 'Walsall',
    cardTitle: 'Man and Van in Walsall',
    title: 'Man and Van in Walsall',
    description: 'VIP House Movers offers a reliable Man and Van service in Walsall for small moves, collections, and flexible, short-notice jobs.',
    intro: 'From student rooms to small flats, our Walsall Man and Van service gives you a friendly, professional option whenever a full removal lorry is more than you need.',
    highlights: baseHighlights('Walsall'),
    process: baseProcess('Walsall'),
    idealFor: baseIdealFor('Walsall'),
  },
  {
    path: '/services/man-and-van/cannock',
    navLabel: 'Cannock',
    cardTitle: 'Man and Van in Cannock',
    title: 'Man and Van in Cannock',
    description: 'Professional Man and Van in Cannock by VIP House Movers, combining careful handling, fair pricing, and flexible availability.',
    intro: 'We help Cannock residents and businesses with small moves, single-item deliveries, and last-minute pickups through a Man and Van service that keeps the same high standards as our larger removals.',
    highlights: baseHighlights('Cannock'),
    process: baseProcess('Cannock'),
    idealFor: baseIdealFor('Cannock'),
  },
  {
    path: '/services/man-and-van/dudley',
    navLabel: 'Dudley',
    cardTitle: 'Man and Van in Dudley',
    title: 'Man and Van in Dudley',
    description: 'Reliable Man and Van in Dudley from VIP House Movers, built for flat moves, furniture collections, and short-notice jobs.',
    intro: 'Whether you are shifting a sofa, collecting a marketplace purchase, or moving a small flat in Dudley, our Man and Van service gives you friendly, experienced movers with a clean, well-equipped van.',
    highlights: baseHighlights('Dudley'),
    process: baseProcess('Dudley'),
    idealFor: baseIdealFor('Dudley'),
  },
  {
    path: '/services/man-and-van/west-bromwich',
    navLabel: 'West Bromwich',
    cardTitle: 'Man and Van in West Bromwich',
    title: 'Man and Van in West Bromwich',
    description: 'Efficient Man and Van in West Bromwich by VIP House Movers, handling small moves, single items, and flexible collections with care.',
    intro: 'Our West Bromwich Man and Van service is ideal for people who want a professional moving crew without the cost of a full-size removals truck, with clear pricing and smooth handling from start to finish.',
    highlights: baseHighlights('West Bromwich'),
    process: baseProcess('West Bromwich'),
    idealFor: baseIdealFor('West Bromwich'),
  },
  {
    path: '/services/man-and-van/telford',
    navLabel: 'Telford',
    cardTitle: 'Man and Van in Telford',
    title: 'Man and Van in Telford',
    description: 'VIP House Movers provides a trusted Man and Van service in Telford, with flexible scheduling and premium care for your items.',
    intro: 'From apartment moves to furniture collections, our Telford Man and Van service gives you a flexible, cost-effective option backed by the same professional standards as our full-scale removals.',
    highlights: baseHighlights('Telford'),
    process: baseProcess('Telford'),
    idealFor: baseIdealFor('Telford'),
  },
  {
    path: '/services/man-and-van/lichfield',
    navLabel: 'Lichfield',
    cardTitle: 'Man and Van in Lichfield',
    title: 'Man and Van in Lichfield',
    description: 'Premium Man and Van in Lichfield from VIP House Movers, combining careful handling, dependable vans, and a polished customer experience.',
    intro: 'Whether you live in the city centre or the surrounding villages, our Lichfield Man and Van service is tailored around your access, parking, and timing for a smooth, respectful move.',
    highlights: baseHighlights('Lichfield'),
    process: baseProcess('Lichfield'),
    idealFor: baseIdealFor('Lichfield'),
  },
  {
    path: '/services/man-and-van/tamworth',
    navLabel: 'Tamworth',
    cardTitle: 'Man and Van in Tamworth',
    title: 'Man and Van in Tamworth',
    description: 'Reliable Man and Van in Tamworth by VIP House Movers, perfect for small moves, short-notice jobs, and single-item deliveries.',
    intro: 'Our Tamworth Man and Van service gives you professional movers, clean vans, and clear pricing whenever you need a smaller, more flexible moving option.',
    highlights: baseHighlights('Tamworth'),
    process: baseProcess('Tamworth'),
    idealFor: baseIdealFor('Tamworth'),
  },
];

export const manAndVanPages = manAndVanPageConfigs.map((config) => ({
  ...config,
  component: createManAndVanPage(config),
}));
